import React, { ReactElement, useState } from 'react'
// Externals imports.
import { PopoverOrigin } from '@material-ui/core/Popover/Popover'
import { Menu, MenuItem } from '@material-ui/core'
// Sub imports.
import { ContextMenuOption } from './Types'
import { ContextMenuPosition } from './Constants'
import { isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
// CSS imports. Commented out because of current build process.
// import './contextMenu.scss';

/**
 * Component properties type.
 */
type Props = {
    className?: string
    onStateChange?: (bVisible: boolean) => void
    menuItems: ContextMenuOption[]
    anchorElement: ReactElement
    position?: ContextMenuPosition
}

/**
 * Position properties type.
 */
type PositionProps = { transformOrigin: PopoverOrigin; anchorOrigin: PopoverOrigin } | Record<string, unknown>

/**
 * Context menu component.
 *
 * @param className Class name for the context menu
 * @param onStateChange Callback for visibility state change of the context menu.
 * @param menuItems List of menu items.
 * @param anchorElement The main trigger element.
 * @param position Render position of the context menu.
 */
const ContextMenu: React.FC<Props> = ({
    className,
    onStateChange,
    menuItems,
    anchorElement,
    position
}: Props): ReactElement => {
    const [pAnchorHTMLElement, setAnchorHTMLElement] = useState<Element | null>(null)

    /**
     * Handles the close event on menu overlay.
     */
    const handleClose = (): void => {
        if (onStateChange) {
            onStateChange(false)
        }
        setAnchorHTMLElement(null)
    }

    /**
     * Handles the open event on menu overlay.
     */
    const handleOpen = (): void => {
        if (onStateChange) {
            onStateChange(true)
        }
    }

    /**
     * Handles click on menu item.
     *
     * @param menuItem The menu item object.
     */
    const handleMenuItemClick = (event: React.MouseEvent, menuItem: ContextMenuOption): void => {
        if (menuItem.isEnabled) {
            if (menuItem.onClick) {
                menuItem.onClick(event)
            }
            setAnchorHTMLElement(null)
        }
    }

    /**
     * Maps the "library independent" position property to underlying MUI library settings for position.
     *
     * @return Position properties for MUI.
     */
    const mapPositionToProps = (): PositionProps => {
        let props = {}

        if (position) {
            let anchorOrigin // MUI internal property - no Akixi naming guidelines applied.
            let transformOrigin // MUI internal property - no Akixi naming guidelines applied.

            const anchorOriginTop = {
                vertical: 'top',
                horizontal: 'center'
            }

            const anchorOriginBottom = {
                vertical: 'bottom',
                horizontal: 'center'
            }

            switch (position) {
                case ContextMenuPosition.BOTTOM_CENTER:
                    anchorOrigin = anchorOriginBottom
                    transformOrigin = {
                        vertical: 'top',
                        horizontal: 'center'
                    }

                    break

                case ContextMenuPosition.BOTTOM_LEFT:
                    anchorOrigin = anchorOriginBottom
                    transformOrigin = {
                        vertical: 'top',
                        horizontal: 'right'
                    }

                    break

                case ContextMenuPosition.BOTTOM_RIGHT:
                    anchorOrigin = anchorOriginBottom
                    transformOrigin = {
                        vertical: 'top',
                        horizontal: 'left'
                    }

                    break

                case ContextMenuPosition.TOP_CENTER:
                    anchorOrigin = anchorOriginTop
                    transformOrigin = {
                        vertical: 'bottom',
                        horizontal: 'center'
                    }

                    break

                case ContextMenuPosition.TOP_LEFT:
                    anchorOrigin = anchorOriginTop
                    transformOrigin = {
                        vertical: 'bottom',
                        horizontal: 'right'
                    }

                    break

                case ContextMenuPosition.TOP_RIGHT:
                    anchorOrigin = anchorOriginTop
                    transformOrigin = {
                        vertical: 'bottom',
                        horizontal: 'left'
                    }

                    break

                default:
                    break
            }

            props = { anchorOrigin, transformOrigin }
        }

        return props
    }

    /**
     * Builds overlay menu.
     */
    const buildMenu = (): ReactElement => (
        <Menu
            id='simple-menu'
            className={`context-menu ${isStringNotEmpty(className) ? className : EMPTY_STRING}`}
            anchorEl={pAnchorHTMLElement}
            keepMounted
            open={Boolean(pAnchorHTMLElement)}
            onClose={handleClose}
            TransitionProps={{
                onEntered: handleOpen
            }}
            {...mapPositionToProps()}
        >
            {menuItems
                .filter((menuItem) => !menuItem.isHidden)
                .map((menuItem) => (
                    <MenuItem key={menuItem.itemName} disableRipple>
                        <a
                            className={`context-menu-item-wrapper ${!menuItem.isEnabled ? 'disabled' : ''}`}
                            target={menuItem.target}
                            href={menuItem.URL}
                            onClick={(event: React.MouseEvent): void => handleMenuItemClick(event, menuItem)}
                            onMouseOver={menuItem?.onMouseOver}
                            onMouseOut={menuItem?.onMouseOut}
                        >
                            {menuItem?.icon}
                            <span className='cm-name'>{menuItem.itemName}</span>
                        </a>
                    </MenuItem>
                ))}
        </Menu>
    )

    // Adding onClick listener for trigger component so that the open/close can be handled here.
    const anchorReactElement = React.cloneElement(anchorElement, {
        onClick: (event: MouseEvent): void => {
            event.stopPropagation()
            event.preventDefault()

            const anchorEl = event.currentTarget
            setAnchorHTMLElement(anchorEl as Element)
        }
    })

    return (
        <React.Fragment>
            {anchorReactElement}
            {buildMenu()}
        </React.Fragment>
    )
}

export default ContextMenu
